import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyValue } from "./EmptyValue";

const numberFormat = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

interface RatingProps {
  /** Nota na escala do sistema (0 a 10). */
  value: number | null | undefined;
  max?: number;
  /** stars: 5 estrelas proporcionais + número; number: só "8,2/10". */
  display?: "stars" | "number";
  size?: "sm" | "md";
  className?: string;
}

/**
 * Nota. A estrela significa só isso: "favorita" usa outro ícone (Pin).
 * Mostra sempre 5 estrelas, preenchidas na proporção da nota, e o número ao lado.
 */
export function Rating({ value, max = 10, display = "stars", size = "sm", className }: RatingProps) {
  if (value == null) return <EmptyValue label="Sem nota" />;
  const ratio = Math.max(0, Math.min(1, value / max));
  const number = (
    <span className="tabular-nums font-semibold">
      {numberFormat.format(value)}
      <span className="font-normal text-muted-foreground">/{max}</span>
    </span>
  );
  if (display === "number") return <span className={cn("text-sm", className)}>{number}</span>;

  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm", className)} aria-label={`Nota ${numberFormat.format(value)} de ${max}`}>
      <span className="relative inline-flex" aria-hidden>
        <span className="flex text-muted-foreground/30">
          {Array.from({ length: 5 }, (_, index) => (
            <Star key={index} className={cn(iconSize, "fill-current")} />
          ))}
        </span>
        <span className="absolute inset-0 flex overflow-hidden text-tone-warning-fg" style={{ width: `${ratio * 100}%` }}>
          {Array.from({ length: 5 }, (_, index) => (
            <Star key={index} className={cn(iconSize, "shrink-0 fill-current")} />
          ))}
        </span>
      </span>
      {number}
    </span>
  );
}

interface RatingInputProps {
  value: number | null | undefined;
  onChange: (value: number) => void;
  max?: number;
  disabled?: boolean;
  className?: string;
}

/** Entrada de nota 1 a 10 como régua de botões (substitui 10 estrelas clicáveis). */
export function RatingInput({ value, onChange, max = 10, disabled, className }: RatingInputProps) {
  return (
    <div role="radiogroup" className={cn("inline-flex overflow-hidden rounded-md border", className)}>
      {Array.from({ length: max }, (_, index) => index + 1).map((option) => {
        const selected = value === option;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(option)}
            className={cn(
              "h-8 w-8 border-r text-sm tabular-nums transition-colors last:border-r-0 hover:bg-muted disabled:opacity-50",
              value != null && option <= value && "bg-tone-warning-bg text-tone-warning-fg",
              selected && "font-bold",
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}
